import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { getDistinctLocationCodes, getLocationPathLabel } from '@card-software/nexus-utilities';
import { LocationNode } from '@app/models/inventory/location';
import { LocationProxyService } from '@app/proxies/inventory/location.proxy.service';

/**
 * Resolves location codes to their full hierarchy label
 * ("Northern Textiles > Finished Goods Warehouse > B > FINISHING > RAMPLA1") so every screen shows
 * the same path instead of the raw code or the leaf name alone.
 *
 * Locations are fetched in batch from Inventory (detailed = true) and cached for the app's lifetime;
 * each code is requested once. `label()` reads a signal, so templates and computed() re-render as soon
 * as a batch arrives, and until then they show the fallback (the raw code by default).
 */
@Injectable({ providedIn: 'root' })
export class LocationPathService {
  private readonly locationProxyService = inject(LocationProxyService);
  private readonly translateService = inject(TranslateService);

  private readonly locationsByCode = signal<ReadonlyMap<string, LocationNode>>(new Map());
  private readonly requestedCodes = new Set<string>();

  /** Fetches (once) the full path of the given codes. Empty and already-requested codes are ignored. */
  load(codes: (string | null | undefined)[]): void {
    const missing = getDistinctLocationCodes(codes, (code) => code).filter((code) => !this.requestedCodes.has(code));
    if (missing.length === 0) return;
    missing.forEach((code) => this.requestedCodes.add(code));

    this.locationProxyService.batchGetByCodes(missing, true).subscribe({
      next: (response) => {
        const locations = response?.data ?? [];
        if (locations.length === 0) return;
        this.locationsByCode.update((current) => {
          const next = new Map(current);
          locations.forEach((location) => next.set(location.locationCode, location));
          return next;
        });
      },
      // Let a later load() retry these codes.
      error: () => missing.forEach((code) => this.requestedCodes.delete(code)),
    });
  }

  /**
   * Full path of the location in the current language. Falls back to the leaf name, then to
   * `fallback`, then to the code itself while the path is loading or when the code is unknown.
   */
  label(code: string | null | undefined, fallback?: string | null): string {
    if (!code) return fallback ?? '';
    const location = this.locationsByCode().get(code);
    return getLocationPathLabel(this.translateService, location?.pathNames, location?.name) || fallback || code;
  }
}
